import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteConfig } from '@/mocks/siteConfig';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const video = videos.find((v) => v.slug === slug);
  const article = articles.find((a) => a.slug === slug);
  const item = video || article;

  if (!item) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-foreground-300 text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-3">
              内容未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              您访问的页面不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isVideo = 'duration' in item;
  const relatedVideos = videos
    .filter((v) => v.category === ('category' in item ? item.category : '') && v.id !== item.id)
    .slice(0, 6);

  const hasRelatedArticles = 'content' in item;

  const categoryInfo = categories.find(
    (c) => c.id === (isVideo ? item.category : (item as typeof articles[0]).category)
  );

  const schemaData = isVideo
    ? {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: item.title,
        description: item.description,
        image: item.imageUrl,
        datePublished: item.year,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: item.rating,
          bestRating: '10',
          ratingCount: item.views,
        },
        url: `${siteConfig.url}/article/${item.slug}`,
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: item.title,
        description: item.summary,
        image: item.imageUrl,
        datePublished: item.date,
        author: {
          '@type': 'Person',
          name: item.author,
        },
        url: `${siteConfig.url}/article/${item.slug}`,
        publisher: {
          '@type': 'Organization',
          name: siteConfig.name,
        },
      };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryInfo?.name || '分类',
        item: `${siteConfig.url}/category/${categoryInfo?.slug || ''}`,
      },
      { '@type': 'ListItem', position: 3, name: item.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            ...(categoryInfo
              ? [{ label: categoryInfo.name, href: `/category/${categoryInfo.slug}` }]
              : []),
            { label: item.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {item.title}
              </h1>

              <div className="flex items-center gap-4 text-xs text-foreground-500 mb-4 flex-wrap">
                {isVideo && (
                  <>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-400"></i>
                      </div>
                      {(item as typeof videos[0]).rating} 分
                    </span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                    <span>{(item as typeof videos[0]).year}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                    <span>{(item as typeof videos[0]).duration}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                    <span>{(item as typeof videos[0]).quality}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                    <span>
                      {(item as typeof videos[0]).views >= 10000
                        ? `${((item as typeof videos[0]).views / 10000).toFixed(1)}万`
                        : (item as typeof videos[0]).views}
                      次播放
                    </span>
                  </>
                )}
                {!isVideo && (
                  <>
                    <span>{(item as typeof articles[0]).author}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                    <span>{(item as typeof articles[0]).date}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                    <span>{(item as typeof articles[0]).views} 次阅读</span>
                  </>
                )}
              </div>

              {'tags' in item && (
                <div className="flex items-center gap-2 flex-wrap">
                  {(item as typeof videos[0]).tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-[11px] bg-secondary-100 text-secondary-700 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {/* Cover Image */}
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={item.imageUrl}
                alt={item.title}
                className="w-full h-full object-cover"
                title={item.title}
              />
            </div>

            {/* Content */}
            {isVideo ? (
              <div className="mb-10">
                <div className="flex items-center justify-center py-8 bg-foreground-950 rounded-lg mb-6">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500">
                      <i className="ri-play-fill text-background-50 text-3xl ml-1"></i>
                    </div>
                    <p className="text-sm text-background-200">点击播放按钮开始观看</p>
                    <p className="text-xs text-foreground-500 mt-1">
                      {(item as typeof videos[0]).quality} · {(item as typeof videos[0]).duration}
                    </p>
                  </div>
                </div>

                <div className="prose prose-sm max-w-none">
                  <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-3">
                    影片简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ) : (
              <div className="mb-10">
                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed [&_p]:mb-4 [&_p]:text-sm [&_p]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: (item as typeof articles[0]).content }}
                />
              </div>
            )}

            {/* Related Videos */}
            {relatedVideos.length > 0 && (
              <section className="border-t border-background-200/70 pt-8">
                <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {relatedVideos.map((v) => (
                    <VideoCard key={v.id} {...v} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}